import * as React from "react";
import { getNotificationForUser, getNotificationOfProject } from "../../../../../app/polar-SAAS-Components/api-helper";
import { useLocation } from "react-router-dom";
import AcknowledgementViewPopup from "src/app/popup/AcknoledgementViewPopup";
import useStyleClasses from "src/app/polar-SAAS-Components/style";
import { useIsMobile } from "../../../../polar-SAAS-Components/utilsNew";


type NotificationPolar = {
  id?: string | number;
  message: string;
  type: string;
  reason?: string;
  projectId?: string | number;
  projectName?: string;
  kitName?: string;
  startTime?: string;
  endTime?: string;
  emailAckId?: string | null;
};

type MagmaResponse<T> = { content: T };

const PAGE_SIZE =  10;
const PAGE_SIZE_MOBILE=4;

const Notification: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile(768);
  const styleClasses = useStyleClasses(isMobile);
  const projectId = React.useMemo(() => {
    const p = new URLSearchParams(location.search).get("projectId");
    return p && p.trim() ? p.trim() : null;
  }, [location.search]);
  const [showAckPopup, setShowAckPopup] = React.useState(false);
  const [selectedAckId, setSelectedAckId] = React.useState<string | null>(null);
  const [selectedNotification,setSelectedNotification]=React.useState<any>(null);
  const openAckPopup = (emailAckId: string, _item: NotificationPolar) => {
    setSelectedAckId(emailAckId);
    setSelectedNotification(_item);
    setShowAckPopup(true);
  };
  const closePopup = () => {
    setShowAckPopup(false);
    setSelectedAckId(null);
  }
 

  const [hoveredRow, setHoveredRow] = React.useState<number | null>(null);
  const [notifications, setNotifications] = React.useState<NotificationPolar[]>([]);
  const [pageLoadedMax, setPageLoadedMax] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isAllRetrieved, setIsAllRetrieved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setCurrentPage(1);
    setNotifications([]);
    setIsAllRetrieved(false);
    setPageLoadedMax(0);
    void fetchPage(0);
  }, [projectId]);

  const fetchPage = async (offset: number): Promise<boolean> => {
    if (isLoading || isAllRetrieved) return false;
    setIsLoading(true);
    setError(null);
    try {
      const res = projectId
        ? await getNotificationOfProject(projectId, offset)
        : await getNotificationForUser(offset);

      const data = (res.data as MagmaResponse<NotificationPolar[]>).content || [];

      if (data.length < PAGE_SIZE) setIsAllRetrieved(true);
      if (data.length > 0) {
        setNotifications((prev) => {
          const next = prev.slice();
          const startIndex = offset * PAGE_SIZE;
          for (let i = 0; i < data.length; i++) {
            next[startIndex + i] = data[i];
          }
          return next;
        });
        setPageLoadedMax((prev) => (offset > prev ? offset : prev));
        return true;
      }
      return false;
    } catch {
      setError("Failed to load notifications. Please try again.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const totalPagesLoaded = Math.ceil(notifications.length / PAGE_SIZE);
  const effectiveTotalPages = isAllRetrieved
    ? totalPagesLoaded
    : Math.max(totalPagesLoaded, pageLoadedMax + 1);

  const goToPage = async (page: number) => {
    if (page < 1) return;
    const neededOffset = page - 1;
    if (neededOffset > pageLoadedMax && !isAllRetrieved) {
      const ok = await fetchPage(neededOffset);
      if (!ok) return;
    }
    setCurrentPage(page);
  };

  const nextPage = () => {
    const next = currentPage + 1;
    if (next <= effectiveTotalPages || !isAllRetrieved) {
      void goToPage(next);
    }
  };

  const previousPage = () => {
    const prev = currentPage - 1;
    if (prev >= 1) {
      void goToPage(prev);
    }
  };

  const visiblePages = () => {
    const maxVisiblePages = isMobile ? 3 : 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(effectiveTotalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    const pages: number[] = [];
    for (let p = startPage; p <= endPage; p++) pages.push(p);
    return { pages, startPage, endPage };
  };

  const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;

  const startIndex = (currentPage - 1) * pageSize;
  const displayed = notifications.slice(startIndex, startIndex + pageSize);

  return (
    <div style={{fontFamily: "'Poppins', sans-serif" }}>
      <div   style={styleClasses.tableContainerStyle}>
      <table  style={styleClasses.tableStyle}>
        <thead>
          <tr>
            <th  style={styleClasses.tableHeaderStyle}>Notification</th>
            <th  style={styleClasses.tableHeaderStyle}>Type</th>
            <th  style={styleClasses.tableHeaderStyle}>Reason</th>
            <th  style={styleClasses.tableHeaderStyle}>Project Name</th>
            <th  style={styleClasses.tableHeaderStyle}>Kit Name</th>
            <th  style={styleClasses.tableHeaderStyle}>Date/Duration</th>
            <th  style={styleClasses.tableHeaderStyle}>Acknowledgement</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map((item, index) => {
            const globalIndex = startIndex + index;
            const alt = globalIndex % 2 === 0 ? "#ffffff" : "#f8f9fa";
            const bg = hoveredRow === globalIndex ? "#e9ecef" : alt;

            return (
              <tr
                key={item && (item.id ? item.id : item.emailAckId ? item.emailAckId : globalIndex)}
                onMouseEnter={() => setHoveredRow(globalIndex)}
                onMouseLeave={() => setHoveredRow(null)}
                 style={{ ...styleClasses.rowBaseStyle, backgroundColor: bg }}
              >
                <td style={styleClasses.tableCellStyle}>{item && item.message ? item.message : "-"}</td>
                <td style={{ ...styleClasses.tableCellStyle, ...styleClasses.typeStyle }}>{item && item.type ? item.type : "-"}</td>
                <td  style={styleClasses.tableCellStyle}>
                  {item && item.reason ? <span  style={styleClasses.linkStyle}>{item.reason}</span> : "-"}
                </td>
                <td  style={styleClasses.tableCellStyle}>
                  {item && item.projectName ? (
                    <span  style={styleClasses.linkStyle} title="Go to Project">
                      {item.projectName}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td  style={styleClasses.tableCellStyle}>{item && item.kitName ? item.kitName : "-"}</td>
                <td  style={styleClasses.tableCellStyle}>
                  {(item && item.startTime ? item.startTime : "-") +
                    " - " +
                    (item && item.endTime ? item.endTime : "-")}
                </td>
                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20.25"
                    height="15"
                    viewBox="0 0 20.25 15"
                    className={!item.emailAckId ? "disabled" : ""}
                    onClick={() =>
                      item.emailAckId && openAckPopup(item.emailAckId, item)
                    }
                    style={{
                      cursor: item.emailAckId ? "pointer" : "not-allowed",
                      opacity: item.emailAckId ? 1 : 0.4,
                      color:item.emailAckId ? "#2A7CED":"gray"
                    }}
                  >
                    <title>View</title>
                    <g transform="translate(0.5 0.5)">
                      <path
                        d="M1,11s3.5-7,9.625-7,9.625,7,9.625,7-3.5,7-9.625,7S1,11,1,11Z"
                        transform="translate(-1 -4)"
                        fill="none"
                        stroke="#707070"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <circle
                        cx="3"
                        cy="3"
                        r="3"
                        transform="translate(6.661 4)"
                        fill="none"
                        stroke="#707070"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                    </g>
                  </svg>
                </td>

              </tr>
            );
          })}

          {displayed.length === 0 && !isLoading && !error && (
            <tr>
              <td  style={styleClasses.tableCellStyle} colSpan={7}>
                No Data Available
              </td>
            </tr>
          )}

          {isLoading && displayed.length === 0 && (
            <tr>
              <td  style={{ ...styleClasses.tableCellStyle, color: "#666" }} colSpan={7}>
                Loading…
              </td>
            </tr>
          )}

          {error && (
            <tr>
              <td  style={{ ...styleClasses.tableCellStyle, color: "crimson" }} colSpan={7}>
                {error}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      <div  style={styleClasses.pagerWrapStyle}>
        <button
           style={{ ...styleClasses.pagerBtnStyle, ...(currentPage === 1 ? styleClasses.pagerBtnDisabled : {}) }}
          disabled={currentPage === 1}
          onClick={previousPage}
        >
          &laquo; {!isMobile?"Previous" : ""}
        </button>
  <span
    style={{
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      padding: "0 3px",
      // optional: subtle feedback while loading
      animation: isLoading ? "pagerFade 0.6s ease-in-out infinite alternate" : undefined,
    }}
  >
        {(() => {
          const vp = visiblePages();
          const parts: React.ReactNode[] = [];

          if (vp.startPage > 1) {
            parts.push(
              <button
                key={1}
                 style={{ ...styleClasses.pageNumberStyle, ...(currentPage === 1 ? styleClasses.pageNumberActive : {}) }}
                onClick={() => goToPage(1)}
              >
                1
              </button>
            );
            if (vp.startPage > 2) {
              parts.push(
                <span key="start-ellipsis"  style={styleClasses.pageEllipsisStyle}>
                  …
                </span>
              );
            }
          }

          vp.pages.forEach((p) => {
            parts.push(
              <button
                key={p}
                 style={{ ...styleClasses.pageNumberStyle, ...(currentPage === p ? styleClasses.pageNumberActive : {}) }}
                onClick={() => goToPage(p)}
              >
                {p}
              </button>
            );
          });

          if (vp.endPage < effectiveTotalPages) {
            if (vp.endPage < effectiveTotalPages - 1) {
              parts.push(
                <span key="end-ellipsis"  style={styleClasses.pageEllipsisStyle}>
                  …
                </span>
              );
            }
            parts.push(
              <button
                key={effectiveTotalPages}
                 style={{ ...styleClasses.pageNumberStyle, ...(currentPage === effectiveTotalPages ? styleClasses.pageNumberActive : {}) }}
                onClick={() => goToPage(effectiveTotalPages)}
              >
                {effectiveTotalPages}
              </button>
            );
          }

          return parts;
        })()}
        </span>

        <button
           style={{ 
            ...styleClasses.pagerBtnStyle,
            ...((currentPage >= effectiveTotalPages && isAllRetrieved) ? styleClasses.pagerBtnDisabled : {}),
          }}
          disabled={currentPage >= effectiveTotalPages && isAllRetrieved}
          onClick={nextPage}
        >
          {!isMobile?"Next" : ""} &raquo;
        </button>

        {!isMobile&&isLoading && (
          <span  style={{ marginLeft: 8, color: "#666", fontSize: 13 }}>Loading…</span>
        )}
        {isAllRetrieved && effectiveTotalPages > 0 && (
          <span  style={{ marginLeft: 8, color: "#888", fontSize: 13 }}>
            All Data Retrieved
          </span>
        )}
      </div>
      {showAckPopup && 
      <AcknowledgementViewPopup onClose={closePopup} emailAckId={selectedAckId}  notification={selectedNotification}/>}
    </div>
  );
};

export default Notification;
